import type { FC, SVGProps } from "react"
import { useContext } from "react"
import { format } from "date-fns"
import {
	IconArrowUpRightOutline18 as ArrowUpRight,
	IconCheck2Outline18 as Check,
	IconGlobe2Outline18 as Globe,
	IconGlobePointerOutline18 as GlobePointer,
	IconGlobeSearchOutline18 as GlobeSearch,
	IconShieldCheckOutline18 as ShieldCheck,
	IconTriangleWarningOutline18 as TriangleWarning,
} from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/cn"
import { Button, CopyButton, Loader, Tooltip } from "@productlane/ui"

import {
	SETTINGS_CARD_CLASS,
	SETTINGS_INSET_CLASS,
	SettingsCardInsetContext,
} from "@/screens/settings/settings-switches/settings-container"

export type DomainState =
	| "active"
	| "waiting_dns"
	| "waiting_certificate"
	| "error"

export type DomainStepStatus = "done" | "current" | "error" | "pending"

export interface DomainStep {
	key: "added" | "dns" | "certificate"
	label: string
	status: DomainStepStatus
	caption?: string
}

export interface DomainSummary {
	state: DomainState
	title: string
	detail: string
}

export const STATUS_WORD: Record<DomainState, string> = {
	active: "Live",
	waiting_dns: "Pending",
	waiting_certificate: "Pending",
	error: "Failed",
}

const STEP_ICON: Record<DomainStep["key"], FC<SVGProps<SVGSVGElement>>> = {
	added: GlobePointer,
	dns: GlobeSearch,
	certificate: ShieldCheck,
}

export function formatDomainEventTime(value?: string | null) {
	if (!value) return undefined
	const date = new Date(value)
	if (Number.isNaN(date.getTime())) return undefined
	return format(date, "MMM d, h:mm a")
}

// Registrars that host a second-level suffix (co.uk, com.au) still treat it as the apex.
const TWO_LEVEL_SUFFIXES = new Set([
	"co.uk",
	"org.uk",
	"me.uk",
	"co.jp",
	"co.nz",
	"co.za",
	"com.au",
	"com.br",
	"com.mx",
	"com.tr",
])

// What the customer types in their registrar's name field: a label, or @ for the domain itself.
export function dnsHostLabel(hostname: string) {
	const parts = hostname.toLowerCase().split(".").filter(Boolean)
	const suffixLength = TWO_LEVEL_SUFFIXES.has(parts.slice(-2).join(".")) ? 3 : 2
	if (parts.length <= suffixLength) return "@"
	return parts.slice(0, parts.length - suffixLength).join(".")
}

const PREVIEW_SUMMARY: Record<DomainState, DomainSummary> = {
	active: {
		state: "active",
		title: "Your domain is live",
		detail: "This domain serves your portal over HTTPS.",
	},
	waiting_dns: {
		state: "waiting_dns",
		title: "We can't find your CNAME record yet",
		detail: "Nothing answers for this domain yet.",
	},
	waiting_certificate: {
		state: "waiting_certificate",
		title: "Your DNS is correct, the certificate is on its way",
		detail: "Cloudflare issues the certificate within a few minutes.",
	},
	error: {
		state: "error",
		title: "Cloudflare rejected the certificate request",
		detail:
			'caa_error: issue: ssl.com. Your DNS has a CAA record limiting who may issue your certificate. Add 0 issue "letsencrypt.org", 0 issue "pki.goog", 0 issue "ssl.com", then refresh.',
	},
}

// Dev-only: ?domainState=waiting_certificate renders a state the local DNS cannot reach.
export function previewDomainSummary(
	summary: DomainSummary,
	search: string,
	isDev: boolean,
): DomainSummary {
	if (!isDev) return summary
	const requested = new URLSearchParams(search).get("domainState")
	if (!requested || !(requested in PREVIEW_SUMMARY)) return summary
	return PREVIEW_SUMMARY[requested as DomainState]
}

export function domainRefreshToast(summary: DomainSummary) {
	return {
		kind: summary.state === "active" ? ("success" as const) : ("info" as const),
		title: summary.title,
		description: summary.detail,
	}
}

export function buildDomainSteps({
	state,
	dnsFound,
	createdAt,
}: {
	state: DomainState
	dnsFound: boolean | null
	createdAt?: string | null
}): DomainStep[] {
	const dnsMissing = dnsFound === false
	const dnsStatus: DomainStepStatus =
		state === "active" || state === "waiting_certificate"
			? "done"
			: state === "error"
				? dnsMissing
					? "error"
					: "done"
				: "current"

	const certificateStatus: DomainStepStatus =
		state === "active"
			? "done"
			: state === "error"
				? dnsMissing
					? "pending"
					: "error"
				: state === "waiting_certificate"
					? "current"
					: "pending"

	return [
		{
			key: "added",
			label: "Domain added",
			status: "done",
			caption: formatDomainEventTime(createdAt),
		},
		{
			key: "dns",
			label:
				dnsStatus === "done"
					? "DNS verified"
					: dnsStatus === "error"
						? "DNS record missing"
						: "Checking DNS",
			status: dnsStatus,
		},
		{
			key: "certificate",
			label:
				certificateStatus === "done"
					? "Certificate issued"
					: certificateStatus === "error"
						? "Certificate failed"
						: "Issuing certificate",
			status: certificateStatus,
		},
	]
}

interface DomainProgressProps {
	hostname: string
	summary: DomainSummary
	dnsFound: boolean | null
	createdAt?: string | null
	className?: string
}

export function DomainProgress({
	hostname,
	summary,
	dnsFound,
	createdAt,
	className,
}: DomainProgressProps) {
	const inset = useContext(SettingsCardInsetContext)
	const steps = buildDomainSteps({ state: summary.state, dnsFound, createdAt })
	return (
		<div
			className={cn(
				SETTINGS_CARD_CLASS,
				"p-4",
				inset && SETTINGS_INSET_CLASS,
				className,
			)}
		>
			<div className="flex items-center justify-between gap-4">
				<div className="flex min-w-0 items-center gap-3">
					<span className="flex h-10 w-10 flex-none items-center justify-center rounded-[10px] bg-gray-100 text-primary dark:bg-gray-800 [&_svg]:h-[18px] [&_svg]:w-[18px]">
						<Globe />
					</span>
					<div className="flex min-w-0 flex-col">
						<span className="text-xxs font-medium uppercase tracking-wide text-tertiary">
							Domain
						</span>
						<span className="truncate text-base font-medium">{hostname}</span>
					</div>
				</div>
				<div className="flex flex-none items-center gap-2">
					<CopyButton
						value={hostname}
						tooltip="Copy domain"
						className="!text-tertiary hover:!text-primary"
					/>
					<Button
						variant="outline"
						iconRight={ArrowUpRight}
						onClick={() =>
							window.open(
								`https://${hostname}`,
								"_blank",
								"noopener,noreferrer",
							)
						}
					>
						Visit site
					</Button>
				</div>
			</div>

			<dl className="mt-5 flex flex-wrap gap-x-12 gap-y-4">
				<MetaCell label="Created" value={formatDomainEventTime(createdAt)} />
				<MetaCell
					label="Status"
					value={STATUS_WORD[summary.state]}
					tooltip={`${summary.title}. ${summary.detail}`}
				/>
			</dl>

			<ol className="mt-5 flex items-start rounded-lg bg-[radial-gradient(circle_at_1px_1px,rgba(0,0,0,0.07)_1px,transparent_0)] bg-[length:9px_9px] py-5 ring-1 ring-inset ring-black/5 dark:bg-[radial-gradient(circle_at_1px_1px,rgba(255,255,255,0.08)_1px,transparent_0)] dark:ring-white/10">
				{steps.map((step, index) => (
					<li
						key={step.key}
						className="flex min-w-0 flex-1 flex-col items-center gap-2"
					>
						<div className="flex w-full items-center">
							<Connector filled={index > 0 && step.status !== "pending"} />
							<StepNode step={step} />
							<Connector
								filled={
									index < steps.length - 1 &&
									steps[index + 1]!.status !== "pending"
								}
							/>
						</div>
						<span
							className={cn(
								"px-1 text-center text-xs font-medium",
								step.status === "pending" && "font-normal text-tertiary",
								step.status === "done" && "text-primary",
								step.status === "current" &&
									"text-yellow-700 dark:text-yellow-400",
								step.status === "error" && "text-red-600 dark:text-red-400",
							)}
						>
							{step.label}
						</span>
						{step.caption ? (
							<span className="px-1 text-center text-xxs text-tertiary">
								{step.caption}
							</span>
						) : null}
					</li>
				))}
			</ol>
		</div>
	)
}

function MetaCell({
	label,
	value,
	tooltip,
}: {
	label: string
	value?: string
	tooltip?: string
}) {
	const text = (
		<dd
			className="truncate text-sm text-secondary"
			title={tooltip ? undefined : value}
		>
			{value ?? "Unknown"}
		</dd>
	)

	return (
		<div className="min-w-0">
			<dt className="text-xxs font-medium uppercase tracking-wide text-tertiary">
				{label}
			</dt>
			{tooltip ? (
				<Tooltip label={tooltip} shouldWrapChildren contentClassName="max-w-xs">
					{text}
				</Tooltip>
			) : (
				text
			)}
		</div>
	)
}

function Connector({ filled }: { filled: boolean }) {
	return (
		<span
			aria-hidden
			className={cn(
				"h-px flex-1",
				filled
					? "bg-gray-300 dark:bg-gray-600"
					: "bg-gray-200 dark:bg-gray-700",
			)}
		/>
	)
}

function StepNode({ step }: { step: DomainStep }) {
	const Icon = STEP_ICON[step.key]

	return (
		<span
			className={cn(
				"flex h-9 w-9 flex-none items-center justify-center rounded-[10px] ring-1 ring-inset [&_svg]:h-[18px] [&_svg]:w-[18px]",
				step.status === "pending" &&
					"bg-gray-100 text-tertiary ring-black/5 dark:bg-gray-800 dark:ring-white/10",
				step.status === "done" &&
					"bg-white shadow-card ring-black/5 dark:bg-gray-850 dark:shadow-card-dark dark:ring-white/10",
				step.status === "current" &&
					"bg-yellow-50 text-yellow-700 ring-yellow-600/20 dark:bg-yellow-400/10 dark:text-yellow-400 dark:ring-yellow-400/30",
				step.status === "error" &&
					"bg-red-50 text-red-600 ring-red-600/20 dark:bg-red-400/10 dark:text-red-400 dark:ring-red-400/30",
			)}
		>
			{step.status === "done" ? (
				<Check className="flex-none text-green-600 dark:text-green-500" />
			) : step.status === "error" ? (
				<TriangleWarning className="flex-none" />
			) : step.status === "current" ? (
				<Loader size={4} />
			) : (
				<Icon className="flex-none" />
			)}
		</span>
	)
}
